// File: frontend/src/views/public/NewsletterResubscribe.js

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_CONFIG } from '../../config/api';
import apiService from '../../services/apiService';
import LoadingSpinner from '../../components/utility/LoadingSpinner';
import styles from './NewsletterResubscribe.module.css';

const NewsletterResubscribe = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('user_id');
  const token = searchParams.get('token');
  const correlationId = searchParams.get('correlation_id');
  
  const [status, setStatus] = useState('pending'); // pending, confirming, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [userData, setUserData] = useState(null);
  const [countdown, setCountdown] = useState(5);
  
  const hasValidated = useRef(false);
  
  useEffect(() => {
    document.title = 'Resubscribe to Newsletter - Allegory Insurance';
    
    if (hasValidated.current) {
      return;
    }
    
    validateResubscribeLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, token]);
  
  // Countdown for redirect after success
  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      
      return () => clearTimeout(timer);
    } else if (status === 'success' && countdown === 0) {
      navigate('/');
    }
  }, [status, countdown, navigate]);
  
  const validateResubscribeLink = async () => {
    hasValidated.current = true;
    
    if (!userId || !token) {
      setStatus('error');
      setErrorMessage('Invalid resubscribe link. Please check the link from your email.');
      return;
    }
    
    // Link is valid, show confirmation screen
    setStatus('pending');
  };
  
  const handleConfirmResubscribe = async () => {
    setStatus('confirming');
    
    try {
      console.log('📧 Resubscribing to newsletter...', { 
        user_id: userId,
        has_token: !!token,
        correlation_id: correlationId
      });
      
      const response = await apiService.resubscribeNewsletter({
        user_id: userId,
        enterprise_id: API_CONFIG.enterpriseId,
        token: token,
        language: navigator.language.toLowerCase() || 'en-ca',
        correlation_id: correlationId
      });
      
      if (response.success) {
        console.log('✅ Successfully resubscribed');
        setUserData(response.data);
        setStatus('success');
      } else {
        throw new Error(response.error || 'Resubscribe failed');
      }
      
    } catch (error) {
      console.error('❌ Resubscribe error:', error);
      setStatus('error');
      
      if (error.message.includes('Invalid or expired')) {
        setErrorMessage('This resubscribe link is invalid or has expired. Please subscribe again from our homepage.');
      } else if (error.message.includes('already subscribed')) {
        setErrorMessage("You're already subscribed to our newsletter. Check your inbox for our latest updates!");
      } else {
        setErrorMessage(error.message || 'Failed to resubscribe. Please try again or contact support.');
      }
    }
  };
  
  const handleBackToHome = () => {
    navigate('/');
  };
  
  const handleContactSupport = () => {
    navigate('/contact');
  };
  
  // Confirming state (processing)
  if (status === 'confirming') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.brandHeader}>
            <span className={styles.logo}>💳</span>
            <h2 className={styles.brandName}>Allegory Insurance</h2>
          </div>
          <LoadingSpinner />
          <p className={styles.processingText}>Processing your request...</p>
        </div>
      </div>
    );
  }
  
  // Success state
  if (status === 'success') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.brandHeader}>
            <span className={styles.logo}>💳</span>
            <h2 className={styles.brandName}>Allegory Insurance</h2>
          </div>
          
          <div className={styles.successIcon}>
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" fill="#ECFDF5"/>
              <path 
                d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" 
                stroke="#10B981" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <path 
                d="M22 4L12 14.01L9 11.01" 
                stroke="#10B981" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </div>
          
          <h1 className={styles.title}>Welcome Back!</h1>
          <p className={styles.message}>
            {userData?.first_name && `${userData.first_name}, `}
            you've been successfully resubscribed to our newsletter. We're excited to have you back!
          </p>
          
          <div className={styles.successBox}>
            <h3 className={styles.successBoxTitle}>What's Next?</h3>
            <div className={styles.benefitsList}>
              <div className={styles.benefitItem}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Check your inbox for a confirmation email</span>
              </div>
              <div className={styles.benefitItem}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Receive weekly insurance tips and advice</span>
              </div>
              <div className={styles.benefitItem}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Get exclusive savings opportunities</span>
              </div>
            </div>
          </div>
          
          <div className={styles.countdownBox}>
            <p className={styles.redirectMessage}>
              Redirecting to homepage in <strong>{countdown}</strong> second{countdown !== 1 ? 's' : ''}...
            </p>
          </div>
          
          <button 
            onClick={handleBackToHome}
            className={styles.primaryButton}
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }
  
  // Error state
  if (status === 'error') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.brandHeader}>
            <span className={styles.logo}>💳</span>
            <h2 className={styles.brandName}>Allegory Insurance</h2>
          </div>
          
          <div className={styles.errorIcon}>
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" fill="#FEF2F2"/>
              <circle cx="12" cy="12" r="10" stroke="#EF4444" strokeWidth="2"/>
              <path d="M12 8V12M12 16H12.01" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          
          <h1 className={styles.title}>Resubscribe Failed</h1>
          <div className={styles.errorBox}>
            <p className={styles.errorMessage}>{errorMessage}</p>
          </div>
          
          <p className={styles.helpText}>
            If you'd like to subscribe to our newsletter, you can do so from our homepage. If you continue to have issues, please contact our support team.
          </p>
          
          <div className={styles.buttonGroup}>
            <button 
              onClick={handleBackToHome}
              className={styles.primaryButton}
            >
              Back to Home
            </button>
            
            <button 
              onClick={handleContactSupport}
              className={styles.secondaryButton}
            >
              Contact Support
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Pending state (confirmation screen)
  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.brandHeader}>
          <span className={styles.logo}>💳</span>
          <h2 className={styles.brandName}>Allegory Insurance</h2>
        </div>
        
        <div className={styles.welcomeIcon}>
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#EFF6FF"/>
            <path 
              d="M8 12L11 15L16 9" 
              stroke="#1E3A8A" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
            <path 
              d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12" 
              stroke="#1E3A8A" 
              strokeWidth="2" 
              strokeLinecap="round"
            />
          </svg>
        </div>
        
        <h1 className={styles.title}>Welcome Back!</h1>
        <p className={styles.message}>
          We're glad you're considering rejoining our newsletter community. By resubscribing, you'll receive:
        </p>
        
        <div className={styles.benefitsList}>
          <div className={styles.benefitItem}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#1E3A8A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Weekly insurance tips and expert advice</span>
          </div>
          <div className={styles.benefitItem}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#1E3A8A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Rate alerts when prices drop</span>
          </div>
          <div className={styles.benefitItem}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#1E3A8A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Exclusive savings opportunities</span>
          </div>
          <div className={styles.benefitItem}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#1E3A8A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Industry news and updates</span>
          </div>
        </div>
        
        <div className={styles.infoBox}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="#1E3A8A" strokeWidth="2"/>
            <path d="M12 16V12M12 8H12.01" stroke="#1E3A8A" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <p className={styles.infoText}>
            You can unsubscribe at any time using the link in any of our emails.
          </p>
        </div>
        
        <div className={styles.buttonGroup}>
          <button 
            onClick={handleConfirmResubscribe}
            className={styles.primaryButton}
          >
            Yes, Resubscribe Me
          </button>
          
          <button 
            onClick={handleBackToHome}
            className={styles.secondaryButton}
          >
            No Thanks
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewsletterResubscribe;